import { Address_Schema } from "@/services/addresses/addressSchema";
import { CategoryId_Schema } from "@/services/collection/categories";
import { ApiResponse_Image_Schema } from "@/services/images";
import { MarketplaceItemId_Schema } from "@/services/marketplace/item";
import { PaymentMethod_Schema } from "@/services/marketplace/paymentMethod/paymentMethodSchema";
import { ShippingMethod_Schema } from "@/services/marketplace/shippingMethod/shippingMethodSchema";
import { PriceNumeric_Schema } from "@/services/prices";
import { UserId_Schema } from "@/services/user/userIdSchema";
import { Nickname_Schema } from "@/services/user/profile";
import { Rating_Schema } from "@/services/user/rating";
import { z } from "zod";

export const CartItemId_Schema = z.number().int().positive();

export const CartItem_Schema = z
  .object({
    category: CategoryId_Schema,
    id: CartItemId_Schema,
    images: ApiResponse_Image_Schema.array(),
    maxQuantity: z.number().int().min(1),
    name: z.string(),
    price: PriceNumeric_Schema,
    priceOriginal: PriceNumeric_Schema,
    quantity: z.number().int().min(1),
    userId: UserId_Schema,
    marketPlaceItemId: MarketplaceItemId_Schema,
    commission: PriceNumeric_Schema,
  })
  .transform(({ name, images, category, maxQuantity, price, priceOriginal, marketPlaceItemId, commission, ...rest }) => {
    // We cannot distinguish between original and converted price.
    // Therefore we try to find out if the currency is the same - then it's negotiated price.
    // However we are loosing some info in case the original price is different currency and it's negotiated as well.
    // See https://app.asana.com/1/1205261752347246/inbox/1205268131011732/item/1213981195475169/story/1213990334767699
    let priceBeforeNegotiation = price;
    if (priceOriginal.currencyId === price.currencyId) {
      priceBeforeNegotiation = priceOriginal;
    }

    return {
      productName: name,
      productImages: images,
      productCategoryId: category,
      productId: marketPlaceItemId,
      maxAvailableQuantity: maxQuantity,
      price,
      priceBeforeConversion: priceOriginal,
      priceBeforeNegotiation,
      priceCommission: commission,
      ...rest,
    };
  });

export const SingleSellerCart_Schema = z
  .object({
    id: UserId_Schema,
    avatarUrl: z.url().nullable(),
    nickname: Nickname_Schema,
    rating: Rating_Schema,
    ratingCount: z.int(),
    basketItems: CartItem_Schema.array(),
    priceCommission: PriceNumeric_Schema,
    priceShipping: PriceNumeric_Schema,
    pricePayment: PriceNumeric_Schema,
    priceTotal: PriceNumeric_Schema,
  })
  .meta({ openapi: "BasketSeller" })
  .transform(({ nickname, avatarUrl, rating, ratingCount, basketItems, priceTotal, id, ...rest }) => ({
    sellerId: id,
    sellerAvatar: avatarUrl,
    sellerNickname: nickname,
    sellerRating: rating,
    sellerRatingCount: ratingCount,
    items: basketItems,
    priceItemsTotal: priceTotal,
    ...rest,
  }));

export const ShoppingCart_Schema = z
  .object({
    id: z.number().int().positive(), // unused
    address: Address_Schema.nullable(),
    delivery: ShippingMethod_Schema.nullable(),
    payment: PaymentMethod_Schema.nullable(),
    basketSellers: SingleSellerCart_Schema.array(),
    commission: PriceNumeric_Schema, // Only fixed part of commision for now!
  })
  .transform(({ delivery, payment, basketSellers, address, commission, ...rest }) => {
    let selectedShippingMethod = null;
    let selectedPaymentMethod = null;

    const someSingleSellerCartExists = basketSellers.length > 0;
    if (someSingleSellerCartExists) {
      if (delivery !== null) {
        const selectedShippingMethodPrice = basketSellers[0].priceShipping;
        selectedShippingMethod = { ...delivery, price: selectedShippingMethodPrice };
      }
      if (payment !== null) {
        const selectedPaymentMethodPrice = basketSellers[0].pricePayment;
        selectedPaymentMethod = { ...payment, price: selectedPaymentMethodPrice };
      }
    }

    return {
      selectedShippingMethod,
      shippingAddress: address,
      selectedPaymentMethod,
      subcarts: basketSellers,
      priceCommissionFixedPart: commission,
      ...rest,
    };
  });
